import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Card, Col, Row, Select, Spin, Modal, Table, Typography, Tooltip, Button } from 'antd'
import { QuestionCircleOutlined, DownloadOutlined } from '@ant-design/icons'
import { useQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import { getDashboardData, getPatientFeedbackData, getLoggedInPatientsData } from '../../services/dashboard.service'
import { fetchCustomers2 } from '../../store/action/customer.action'
import { setPatient } from '../../store/action/patient.action'
import { loginAsCustomer } from '../../store/action/profile.action'
import util from '../../util/util'
import { getEventSource, getEventName } from '../../util/activityLog.util'
import '../../css/users.scss'

const { Option } = Select
const { Title, Text } = Typography

// Amazon-style proportional star rating. Each star is filled to the exact
// fraction of `value` it represents — e.g. 4.2 fills the 5th star 20%.
// Uses an SVG star with an objectBoundingBox gradient so the fill maps to
// the star's true visual width (a ★ glyph has padding that throws this off).
const STAR_PATH =
  'M12 .587l3.668 7.431 8.2 1.192-5.934 5.787 1.401 8.169L12 18.896l-7.335 3.863 1.401-8.169L.132 9.21l8.2-1.192z'

const StarRating = ({ value, max = 5, size = 32, color = '#e8702a', emptyColor = '#d9d9d9' }) => (
  <span style={{ display: 'inline-flex', gap: '4px', lineHeight: 1 }}>
    {Array.from({ length: max }).map((_, i) => {
      const fill = Math.max(0, Math.min(1, value - i)) // 0..1 for this star
      const pct = `${fill * 100}%`
      const gid = `star-fill-${i}-${Math.round(fill * 1000)}`
      return (
        <svg key={i} width={size} height={size} viewBox='0 0 24 24'>
          <defs>
            <linearGradient id={gid}>
              <stop offset={pct} stopColor={color} />
              <stop offset={pct} stopColor={emptyColor} />
            </linearGradient>
          </defs>
          <path d={STAR_PATH} fill={`url(#${gid})`} />
        </svg>
      )
    })}
  </span>
)

const Dashboard = (props) => {
  const history = useHistory()

  const [selectedCustomer, setSelectedCustomer] = useState('all')
  const [dateRange, setDateRange] = useState('7d')
  const [modalVisible, setModalVisible] = useState(false)
  const [modalData, setModalData] = useState([])
  const [modalTitle, setModalTitle] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [isAdminModal, setIsAdminModal] = useState(false)
  const [isLoggedInModal, setIsLoggedInModal] = useState(false)
  // Total Active Patients is a schedule roster enriched with each patient's
  // latest real activity across all time — the roster itself is not scoped to
  // the selected range, so its Last Activity column is not either.
  const [isActiveModal, setIsActiveModal] = useState(false)
  const [isRegisteredModal, setIsRegisteredModal] = useState(false)
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false)
  const [feedbackPage, setFeedbackPage] = useState(1)
  const pageSize = 10

  // Patient Feedback card is restricted to Company Admins (role 21).
  const canViewFeedback = String(props.user?.role) === '21'

  // Logged-In Patients card is restricted to Company Admins (role 21).
  const canViewLoggedInPatients = String(props.user?.role) === '21'

  // Date range options
  const dateRangeOptions = [
    { value: '1d', label: '1 day' },
    { value: '3d', label: '3 days' },
    { value: '7d', label: '7 days' },
    { value: '15d', label: '15 days' },
    { value: '1m', label: '1 month' },
    { value: '2m', label: '2 months' },
    { value: '3m', label: '3 months' },
  ]

  // Get customers from props
  const customers = props.customers || []

  // Fetch customers using Redux
  useEffect(() => {
    if (props.company?._id) {
      props.fetchCustomers2({
        companyid: props.company._id,
        pagination: { current: 1, pageSize: 100 },
        searchText: '',
      })
    }
  }, [props.company?._id])

  // Get dashboard data
  const { data: dashboardData, loading: dashboardLoading, error: dashboardError, refetch } = useQuery(
    getDashboardData,
    {
      variables: {
        input: {
          customerid: selectedCustomer === 'all' ? undefined : selectedCustomer,
          fromDate: dateRange,
        },
      },
      skip: !props.company?._id,
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        console.log('✅ Dashboard API SUCCESS - Response:', data)
        console.log('✅ Input variables sent:', {
          customerid: selectedCustomer === 'all' ? undefined : selectedCustomer,
          fromDate: dateRange,
        })
      },
      onError: (error) => {
        console.error('❌ Dashboard API ERROR:', error)
        console.error('❌ Error details:', error.message)
        console.error('❌ GraphQL errors:', error.graphQLErrors)
        console.error('❌ Network error:', error.networkError)
      }
    }
  )

  // Get dashboard data after useQuery
  const dashboard = dashboardData?.getDashBoardData || {}

  // Patient Feedback (Super Admin only)
  const { data: feedbackData } = useQuery(getPatientFeedbackData, {
    skip: !canViewFeedback,
    fetchPolicy: 'cache-and-network',
  })
  const feedback = feedbackData?.getPatientFeedback || {}
  const feedbackAvg = feedback.average || 0
  const feedbackTotal = feedback.total || 0
  const feedbackItems = feedback.items || []

  // Logged-In Patients (Company Admin only) — ALL live sessions (Active + Away),
  // refreshed every 30s so the count and per-row status stay current.
  const { data: loggedInPatientsData } = useQuery(getLoggedInPatientsData, {
    skip: !canViewLoggedInPatients,
    fetchPolicy: 'no-cache',
    pollInterval: 30000,
  })
  const loggedInPatients = loggedInPatientsData?.getLoggedInPatients?.data || []

  // Debug logging
  console.log('Company ID:', props.company?._id)
  console.log('Customers from Redux:', customers)
  console.log('Dashboard Data:', dashboardData)
  console.log('Dashboard Error:', dashboardError)
  console.log('Selected Customer:', selectedCustomer)
  console.log('Date Range:', dateRange)
  console.log('Dashboard Loading:', dashboardLoading)

  // More detailed dashboard debugging
  if (dashboard) {
    console.log('Active Patients Array:', dashboard.activePatients)
    console.log('Activity Patients Array:', dashboard.activityPatients)
    console.log('New Patients Array:', dashboard.newPatients)
    console.log('Admin Web Logins:', dashboard.adminWebLogins)
    console.log('Admin App Logins:', dashboard.adminAppLogins)
    console.log('Dashboard Status:', dashboard.status)
    console.log('Dashboard Message:', dashboard.message)
  }

  const handleCardClick = (type, title) => {
    let data = []
    switch (type) {
      case 'active':
        data = dashboard.activePatients || []
        break
      case 'activity':
        data = dashboard.activityPatients || []
        break
      case 'new':
        data = dashboard.newPatients || []
        break
      case 'registered':
        data = dashboard.registeredPatients || []
        break
      case 'loggedin':
        data = loggedInPatients
        break
    }
    setModalData(data)
    setModalTitle(title)
    setIsAdminModal(false)
    setIsLoggedInModal(type === 'loggedin')
    setIsActiveModal(type === 'active')
    setIsRegisteredModal(type === 'registered')
    setModalVisible(true)
    setCurrentPage(1)
  }

  const handleAdminCardClick = (type, title, data) => {
    setModalData(data)
    setModalTitle(title)
    setIsAdminModal(true)
    setIsLoggedInModal(false)
    setIsActiveModal(false)
    setIsRegisteredModal(false)
    setModalVisible(true)
    setCurrentPage(1)
  }

  // Helper function to get customer name by ID
  const getCustomerName = (customerId) => {
    if (!customerId) return 'N/A'
    const customer = customers.find(c => c._id === customerId)
    return customer ? customer.name : 'Unknown Clinic'
  }

  // Helper function to map role numbers to role names
  const getRoleName = (roleNumber) => {
    const roleMap = {
      1: 'Clinic Administrator',
      2: 'Physiotherapist',
      3: 'Provider',
      4: 'Medical Associate',
      11: 'Patient',
      21: 'Company Administrator',
      22: 'Company Health Coach',
      31: 'Super Administrator',
      41: 'Anonymous User',
      42: 'Authorized User'
    }
    return roleMap[roleNumber] || `Unknown Role (${roleNumber})`
  }

  // Handle row click to navigate to patient detail page
  const handleRowClick = async (record) => {
    // Find the customer object for this patient
    const customer = customers.find(c => c._id === record.customerid)

    if (customer) {
      // Login as the customer first (this sets the customer context)
      await props.loginAsCustomer(customer)
    }

    // Clear any existing patient state
    await props.setPatient({})

    // Navigate to the patient detail page. The Logged-In Patients rows carry
    // the patient id as `_id`; other cards use `patientid`.
    const patientId = record.patientid || record._id
    history.push({ pathname: '/patient/' + patientId, state: record })
  }

  const columns = [
    {
      title: 'Patient Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong>{record.firstname} {record.lastname}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>ID: {record.patientid}</Text>
        </div>
      ),
    },
    {
      title: 'Clinic Name',
      dataIndex: 'customerid',
      key: 'customerid',
      render: (customerId) => (
        <Text>{getCustomerName(customerId)}</Text>
      ),
    },
    {
      title: 'Action Type',
      dataIndex: 'actiontype',
      key: 'actiontype',
      // Friendly source (Patient / Clinic / MoveAI) derived from the activity's
      // actiontype + application, instead of the raw internal 'patietaction' token.
      // Roster rows with no real activity have no actiontype/method → N/A.
      render: (_, record) => {
        if (!record.actiontype && !record.method) return 'N/A'
        const source = getEventSource(record)
        return source === 'Patients' ? 'Patient' : source
      },
    },
    {
      title: 'Action',
      dataIndex: 'method',
      key: 'method',
      // Human-readable event name (Exercise, Questionnaire, Progress Test,
      // Measurement, Login, ...) instead of the raw server method name.
      render: (_, record) => {
        if (!record.actiontype && !record.method) return 'N/A'
        return getEventName(record) || 'N/A'
      },
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivityDate',
      key: 'lastActivityDate',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
  ]

  // Columns for the Total Active Patients modal — a roster of patients with
  // active schedules, enriched server-side with each patient's latest REAL
  // activity (system logs excluded, and not clipped to the selected date range).
  // Rows arrive already ordered most-recently-active first. A patient who has
  // never done anything renders N/A across Action Type / Action / Last Activity;
  // showing their registration date here read as activity they never performed.
  const activeColumns = [
    columns[0], // Patient Name
    columns[1], // Clinic Name
    columns[2], // Action Type
    columns[3], // Action
    columns[4], // Last Activity
  ]

  // Columns for the Total New Patients - Registered modal. Same five columns as
  // the default set, with a second line under Patient / Clinic / Action:
  // whether the patient ever logged in, the treating provider, and the exact
  // registration timestamp. All three fields ride along on the patient document
  // getRegisteredPatients already reads, so they cost no extra query.
  //
  // NOTE: Action carries the registration event and its timestamp; Last Activity
  // is the patient's most recent REAL action since registering, or N/A if they
  // have not done anything yet. The two are deliberately different dates — the
  // server stopped reporting lastActivityDate = createdAt for these rows.
  //
  // Login Status is a SEPARATE axis from Last Activity, and uses the same
  // vocabulary and colours as the Patient List (component/patient/
  // patient.table.js) and the Logged-In Patients card. One meaning for
  // `loginStatus` across the admin.
  //
  // This popup previously used its own set — Logged In / Registered / Never —
  // driven by `lastLoggedinTime`, which rendered every row green: registration
  // ends in a login, so in a list of registered patients that field is always
  // set. Two different vocabularies under one field name is also how a reader
  // ends up trusting the wrong legend.
  const REGISTRATION_LOGIN = {
    active: { color: '#52c41a', label: 'Active' },
    away: { color: '#faad14', label: 'Away' },
    loggedout: { color: '#f5222d', label: 'Logged Out' },
  }

  const renderRegistrationLogin = (record) => {
    const meta =
      REGISTRATION_LOGIN[record.loginStatus] || REGISTRATION_LOGIN.loggedout
    const seen = record.lastActiveAt
      ? 'Last active ' + moment(record.lastActiveAt).fromNow()
      : 'Never active'
    return (
      <Tooltip title={seen}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: '12px',
            color: 'rgba(0, 0, 0, 0.45)',
          }}
        >
          <span
            style={{
              flex: '0 0 auto',
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: meta.color,
              boxShadow: `0 0 0 2px ${meta.color}40`,
            }}
          />
          {meta.label}
        </span>
      </Tooltip>
    )
  }

  const registeredColumns = [
    {
      title: 'Patient Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong>{record.firstname} {record.lastname}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>ID: {record.patientid}</Text>
          <br />
          {renderRegistrationLogin(record)}
        </div>
      ),
    },
    {
      // Provider sits under the clinic unlabelled and in the same font — the
      // clinic/provider pair reads as one address block.
      title: 'Clinic Name',
      dataIndex: 'customerid',
      key: 'customerid',
      render: (customerId, record) => (
        <div>
          <Text>{getCustomerName(customerId)}</Text>
          {record.provider ? (
            <>
              <br />
              <Text>{record.provider}</Text>
            </>
          ) : null}
        </div>
      ),
    },
    columns[2], // Action Type
    {
      title: 'Action',
      dataIndex: 'method',
      key: 'method',
      render: (_, record) => {
        const registeredAt = record.createdAt || record.lastActivityDate
        return (
          <div>
            <Text>{getEventName(record) || 'N/A'}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {registeredAt ? moment(registeredAt).format('M/D/YYYY h:mm A') : 'N/A'}
            </Text>
          </div>
        )
      },
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivityDate',
      key: 'lastActivityDate',
      render: (date) => (date ? moment(date).format('M/D/YYYY h:mm A') : 'N/A'),
    },
  ]

  // Columns for the Logged-In Patients modal — rows come from
  // getLoggedInPatients (name is a pre-built "firstname lastname" string).
  const loggedInColumns = [
    {
      title: 'Patient Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text || 'N/A'}</Text>,
    },
    {
      title: 'Clinic Name',
      dataIndex: 'clinic',
      key: 'clinic',
      render: (text) => <Text>{text || 'N/A'}</Text>,
    },
    {
      title: 'Login Status',
      dataIndex: 'loginStatus',
      key: 'loginStatus',
      render: (status) => {
        const meta =
          status === 'active'
            ? { color: '#52c41a', label: 'Active' }
            : { color: '#faad14', label: 'Away' }
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                display: 'inline-block',
                backgroundColor: meta.color,
                boxShadow: `0 0 0 2px ${meta.color}40`,
              }}
            />
            {meta.label}
          </span>
        )
      },
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActiveAt',
      key: 'lastActiveAt',
      render: (date) => (date ? moment(date).fromNow() : 'N/A'),
    },
  ]

  const adminColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Text strong>{text || 'N/A'}</Text>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => <Text>{getRoleName(role)}</Text>,
    },
    {
      title: 'Clinic/Company Name',
      key: 'organization',
      render: (text, record) => (
        <Text>{record.customerName || record.companyName || 'N/A'}</Text>
      ),
    },
    {
      title: 'Login Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleString() : 'N/A',
    },
  ]

  // Columns for the Patient Feedback modal — rating shown as a number,
  // no stars (per spec).
  const feedbackColumns = [
    {
      title: 'Patient Name',
      dataIndex: 'patientName',
      key: 'patientName',
      render: (text) => <Text strong>{text || 'N/A'}</Text>,
    },
    {
      title: 'Clinic Name',
      dataIndex: 'clinicName',
      key: 'clinicName',
      render: (text) => <Text>{text || 'N/A'}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (date) => <Text>{date ? new Date(date).toLocaleDateString() : '—'}</Text>,
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 100,
      render: (rating) => <Text>{rating != null ? rating : 'N/A'}</Text>,
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      render: (comment) => <Text>{comment || '—'}</Text>,
    },
  ]

  // Export the full feedback list (not just the current page) to CSV.
  const downloadFeedbackCSV = () => {
    const headers = ['Patient Name', 'Clinic Name', 'Date', 'Rating', 'Comment']
    const escape = (val) => `"${String(val == null ? '' : val).replace(/"/g, '""')}"`
    const csvRows = feedbackItems.map((item) =>
      [
        item.patientName,
        item.clinicName,
        item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '',
        item.rating,
        item.comment,
      ].map(escape).join(',')
    )
    const csvContent = [headers.join(','), ...csvRows].join('\n')

    // Prepend BOM so Excel reads UTF-8 correctly.
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `patient-feedback-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Get readable date range label for tooltips
  const getDateRangeLabel = () => {
    const labels = {
      '1d': '1 day',
      '3d': '3 days',
      '7d': '7 days',
      '15d': '15 days',
      '1m': '1 month',
      '2m': '2 months',
      '3m': '3 months',
    }
    return labels[dateRange] || dateRange
  }

  const patientCardData = [
    {
      title: 'Total Active Patients',
      count: dashboard.activePatients?.length || 0,
      color: '#000000',
      type: 'active',
      tooltip: 'Patients with active schedules (exercise, device, or questionnaire)',
    },
    {
      title: 'Total Patients with Activity',
      count: dashboard.activityPatients?.length || 0,
      color: '#000000',
      type: 'activity',
      tooltip: `Patients who have performed some activity in the last ${getDateRangeLabel()}`,
    },
    {
      title: 'Total New Patients',
      type: 'combined',
      leftCount: dashboard.registeredPatients?.length || 0,
      leftLabel: 'Registered',
      leftType: 'registered',
      rightCount: dashboard.newPatients?.length || 0,
      rightLabel: 'Logged In',
      rightType: 'new',
      color: '#52c41a',
      tooltip: `Registered: New patients created in the last ${getDateRangeLabel()}\nLogged In: Patients who logged in for the first time in the last ${getDateRangeLabel()}`,
    },
    // Company Admin only — mirrors the server-side role gate on getLoggedInPatients.
    ...(canViewLoggedInPatients
      ? [
          {
            title: 'Logged-In Patients',
            count: loggedInPatients.length,
            color: '#000000',
            type: 'loggedin',
            tooltip:
              'Patients with an active login session (not signed out). Active = activity in the last 10 min; Away = idle longer than that.',
          },
        ]
      : []),
  ]

  const adminCardData = [
    {
      title: 'Admin Web Logins',
      count: dashboard.adminWebLogins || 0,
      color: '#8c8c8c',
      type: 'adminWeb',
      data: dashboard.adminWebLoginDetails || [],
    },
    {
      title: 'Admin App Logins',
      count: dashboard.adminAppLogins || 0,
      color: '#8c8c8c',
      type: 'adminApp',
      data: dashboard.adminAppLoginDetails || [],
    },
  ]

  return (
    <div className="userContainer">
      <div className="actionRow" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="headerText">Home</div>
        <div className="dashboard-filters">
          <Select
            style={{ width: 250, marginRight: 16 }}
            value={selectedCustomer}
            onChange={(value) => {
              setSelectedCustomer(value)
              refetch()
            }}
            loading={props.isCustomersLoading}
            placeholder="Select Clinic"
          >
            <Option value="all">All Clinics</Option>
            {customers.map((customer) => (
              <Option key={customer._id} value={customer._id}>
                {customer.name}
              </Option>
            ))}
          </Select>
          <Select
            style={{ width: 150 }}
            value={dateRange}
            onChange={(value) => {
              setDateRange(value)
              refetch()
            }}
          >
            {dateRangeOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      <Spin spinning={dashboardLoading}>
        {/* Patient Activity Section */}
        <div style={{ padding: '24px 0 12px 0' }}>
          <Title level={3} style={{ marginBottom: '16px', color: '#000000' }}>
            Patient Activity
          </Title>
          <Row gutter={[24, 24]}>
            {patientCardData.map((card) => (
              <Col xs={24} sm={12} lg={8} key={card.type}>
                <Card
                  hoverable={card.type !== 'combined'}
                  onClick={card.type !== 'combined' ? () => handleCardClick(card.type, card.title) : undefined}
                  style={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    cursor: card.type !== 'combined' ? 'pointer' : 'default',
                    height: '100%'
                  }}
                  bodyStyle={{ padding: '32px' }}
                >
                  {card.type === 'combined' ? (
                    <div style={{ textAlign: 'center' }}>
                      <Text style={{ display: 'block', fontSize: '16px', color: 'rgba(0, 0, 0, 0.65)', marginBottom: '16px', fontWeight: 500 }}>
                        {card.title}
                        {card.tooltip && (
                          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{card.tooltip}</span>}>
                            <QuestionCircleOutlined style={{ marginLeft: '8px', color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </Text>
                      <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                        <div
                          style={{ textAlign: 'center', cursor: 'pointer', flex: 1 }}
                          onClick={() => handleCardClick(card.leftType, `${card.title} - ${card.leftLabel}`)}
                        >
                          <Title level={1} style={{ margin: 0, color: card.color, fontWeight: 700, fontSize: '64px', lineHeight: 1 }}>
                            {card.leftCount}
                          </Title>
                          <Text style={{ display: 'block', fontSize: '14px', color: 'rgba(0, 0, 0, 0.45)', marginTop: '8px' }}>
                            {card.leftLabel}
                          </Text>
                        </div>
                        <div
                          style={{ textAlign: 'center', cursor: 'pointer', flex: 1 }}
                          onClick={() => handleCardClick(card.rightType, `${card.title} - ${card.rightLabel}`)}
                        >
                          <Title level={1} style={{ margin: 0, color: card.color, fontWeight: 700, fontSize: '64px', lineHeight: 1 }}>
                            {card.rightCount}
                          </Title>
                          <Text style={{ display: 'block', fontSize: '14px', color: 'rgba(0, 0, 0, 0.45)', marginTop: '8px' }}>
                            {card.rightLabel}
                          </Text>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center' }}>
                      <Text style={{ display: 'block', fontSize: '16px', color: 'rgba(0, 0, 0, 0.65)', marginBottom: '16px', fontWeight: 500 }}>
                        {card.title}
                        {card.tooltip && (
                          <Tooltip title={card.tooltip}>
                            <QuestionCircleOutlined style={{ marginLeft: '8px', color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </Text>
                      <Title level={1} style={{ margin: 0, color: card.color, fontWeight: 700, fontSize: '64px', lineHeight: 1 }}>
                        {card.count}
                      </Title>
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* Admin Login Activity Section */}
        <div style={{ padding: '12px 0 24px 0' }}>
          <Title level={3} style={{ marginBottom: '16px', color: '#000000' }}>
            Admin Login Activity
          </Title>
          <Row gutter={[24, 24]}>
            {adminCardData.map((card) => (
              <Col xs={24} sm={12} lg={8} key={card.type}>
                <Card
                  hoverable
                  onClick={() => handleAdminCardClick(card.type, card.title, card.data)}
                  style={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    height: '100%'
                  }}
                  bodyStyle={{ padding: '32px' }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <Text style={{ display: 'block', fontSize: '16px', color: 'rgba(0, 0, 0, 0.65)', marginBottom: '16px', fontWeight: 500 }}>
                      {card.title}
                    </Text>
                    <Title level={1} style={{ margin: 0, color: card.color, fontWeight: 700, fontSize: '64px', lineHeight: 1 }}>
                      {card.count}
                    </Title>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* Patient Feedback Section — Company Admin (role 21) only */}
        {canViewFeedback && (
          <div style={{ padding: '12px 0 24px 0' }}>
            <Title level={3} style={{ marginBottom: '16px', color: '#000000' }}>
              Patient Feedback
            </Title>
            <Row gutter={[24, 24]}>
              <Col xs={24} sm={12} lg={8}>
                <Card
                  hoverable
                  onClick={() => {
                    setFeedbackPage(1)
                    setFeedbackModalVisible(true)
                  }}
                  style={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    height: '100%',
                  }}
                  bodyStyle={{ padding: '32px' }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <Text style={{ display: 'block', fontSize: '16px', color: 'rgba(0, 0, 0, 0.65)', marginBottom: '16px', fontWeight: 500 }}>
                      Patient Feedback
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <Title level={1} style={{ margin: 0, color: '#000000', fontWeight: 700, fontSize: '48px', lineHeight: 1 }}>
                        {feedbackAvg.toFixed(1)}
                      </Title>
                      <StarRating value={feedbackAvg} size={32} />
                      <Text style={{ fontSize: '22px', color: '#2f54eb', fontWeight: 600 }}>
                        ({feedbackTotal})
                      </Text>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Spin>

      <Modal
        title={modalTitle}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={1200}
      >
        <Table
          dataSource={modalData}
          columns={isLoggedInModal ? loggedInColumns : isAdminModal ? adminColumns : isActiveModal ? activeColumns : isRegisteredModal ? registeredColumns : columns}
          rowKey="_id"
          onRow={isAdminModal ? undefined : (record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' }
          })}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: modalData.length,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} ${isAdminModal ? 'logins' : 'patients'}`,
          }}
        />
      </Modal>

      <Modal
        title="Patient Feedback"
        visible={feedbackModalVisible}
        onCancel={() => setFeedbackModalVisible(false)}
        footer={null}
        width={1000}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <Button
            icon={<DownloadOutlined />}
            onClick={downloadFeedbackCSV}
            disabled={!feedbackItems.length}
          >
            Download CSV
          </Button>
        </div>
        <Table
          dataSource={feedbackItems}
          columns={feedbackColumns}
          rowKey={(record, index) => `${record.patientid}-${index}`}
          pagination={{
            current: feedbackPage,
            pageSize: pageSize,
            total: feedbackItems.length,
            onChange: (page) => setFeedbackPage(page),
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} feedback`,
          }}
        />
      </Modal>
    </div>
  )
}

const mapStateToProps = (state) => ({
  company: state.profile.company,
  user: state.profile.user,
  customers: state.customer.customers,
  isCustomersLoading: state.customer.isLoading,
})

const mapDispatchToProps = (dispatch) => ({
  fetchCustomers2: (params) => dispatch(fetchCustomers2(params)),
  setPatient: (patient) => dispatch(setPatient(patient)),
  loginAsCustomer: (customer) => dispatch(loginAsCustomer(customer)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard)